import { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtNumber, fmtDate } from "@/lib/api";
import { downloadXlsx, todayStamp } from "@/lib/xlsx";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateField } from "@/components/ui/date-field";
import { CircleNotch, DownloadSimple, BookOpen } from "@phosphor-icons/react";

export default function Kardex() {
  const [productos, setProductos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [productoId, setProductoId] = useState("");
  const [loteId, setLoteId] = useState("todos");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/productos").then((r) => setProductos(r.data));
    api.get("/lotes").then((r) => setLotes(r.data));
  }, []);

  const lotesProducto = lotes.filter((l) => l.producto_id === productoId);

  const cargar = useCallback(() => {
    if (!productoId) {
      toast.error("Selecciona un producto");
      return;
    }
    setLoading(true);
    const params = new URLSearchParams({ producto_id: productoId });
    if (loteId !== "todos") params.set("lote_id", loteId);
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);
    api
      .get(`/kardex?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("No se pudo cargar el Kardex"))
      .finally(() => setLoading(false));
  }, [productoId, loteId, desde, hasta]);

  const tipoLabel = (m) => {
    if (m.tipo === "ingreso") return "Ingreso";
    if (m.tipo === "salida") return "Salida";
    if (m.doc_tipo === "reverso") return "Reverso";
    return `Ajuste ${m.efecto === "entrada" ? "+" : "−"}`;
  };

  const exportExcel = () => {
    if (!data) return;
    const rows = [["Fecha", "Lote", "Documento", "Tipo", "Entrada", "Salida", "Saldo cantidad", "Costo unitario", "Valor movimiento", "Valor acumulado"]];
    data.movimientos.forEach((m) =>
      rows.push([
        (m.fecha_movimiento || "").slice(0, 10), m.numero_lote, m.motivo || m.doc_tipo || "",
        tipoLabel(m), m.entrada || "", m.salida || "", m.saldo_cantidad,
        m.costo_unitario, m.valor_movimiento, m.valor_acumulado,
      ]));
    downloadXlsx(`kardex_${data.producto.codigo}_${todayStamp()}.xlsx`, [
      { name: "Kardex", rows, cols: [{ wch: 12 }, { wch: 14 }, { wch: 22 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 16 }] },
    ]);
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="kardex-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Kardex valorizado</h1>
        <p className="text-[#475569] mt-1">Historial de movimientos con costo y saldo acumulado (CMP) por producto.</p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-md p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Producto</Label>
          <Select value={productoId} onValueChange={(v) => { setProductoId(v); setLoteId("todos"); }}>
            <SelectTrigger data-testid="kardex-producto" className="bg-white border-[#E2E8F0]">
              <SelectValue placeholder="Selecciona" />
            </SelectTrigger>
            <SelectContent>
              {productos.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.nombre} ({p.codigo})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Lote</Label>
          <Select value={loteId} onValueChange={setLoteId}>
            <SelectTrigger data-testid="kardex-lote" className="bg-white border-[#E2E8F0]">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los lotes</SelectItem>
              {lotesProducto.map((l) => (
                <SelectItem key={l.id} value={l.id}>{l.numero_lote}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Desde</Label>
          <DateField value={desde} onChange={setDesde} testid="kardex-desde" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Hasta</Label>
          <DateField value={hasta} onChange={setHasta} testid="kardex-hasta" />
        </div>
        <Button data-testid="kardex-generar" onClick={cargar} disabled={loading} className="h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold">
          {loading ? <CircleNotch size={18} className="animate-spin" /> : "Generar Kardex"}
        </Button>
      </div>

      {!data ? (
        <div className="bg-white border border-[#E2E8F0] rounded-md py-20 flex flex-col items-center gap-3 text-[#64748B]">
          <BookOpen size={36} weight="duotone" />
          <p>Selecciona un producto y genera el Kardex.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Metric label="Saldo cantidad" value={fmtNumber(data.saldo_cantidad)} />
            <Metric label="Saldo valorizado" value={fmtMoney(data.saldo_valor)} />
            <Metric label="CMP actual" value={fmtMoney(data.cmp)} />
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-bold text-[#0F172A]">{data.producto.nombre}</h2>
                <p className="text-xs text-[#64748B]">{data.producto.codigo} · {data.movimientos.length} movimientos</p>
              </div>
              <Button data-testid="kardex-export" variant="outline" onClick={exportExcel} disabled={data.movimientos.length === 0} className="h-10 border-[#E2E8F0] font-semibold">
                <DownloadSimple size={18} weight="bold" className="mr-1.5" /> Excel
              </Button>
            </div>
            {data.movimientos.length === 0 ? (
              <p className="px-6 py-12 text-center text-[#64748B]">Sin movimientos para los filtros.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="kardex-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                      <th className="px-4 py-3 font-semibold">Fecha</th>
                      <th className="px-4 py-3 font-semibold">Documento</th>
                      <th className="px-4 py-3 font-semibold">Tipo</th>
                      <th className="px-4 py-3 font-semibold text-right">Entrada</th>
                      <th className="px-4 py-3 font-semibold text-right">Salida</th>
                      <th className="px-4 py-3 font-semibold text-right">Saldo</th>
                      <th className="px-4 py-3 font-semibold text-right">Costo unit.</th>
                      <th className="px-4 py-3 font-semibold text-right">Valor mov.</th>
                      <th className="px-4 py-3 font-semibold text-right">Valor acum.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.movimientos.map((m) => (
                      <tr key={m.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                        <td className="px-4 py-3 text-[#64748B] whitespace-nowrap">{fmtDate(m.fecha_movimiento)}</td>
                        <td className="px-4 py-3">
                          <div className="text-[#0F172A]">{m.numero_lote}</div>
                          <div className="text-xs text-[#64748B] truncate max-w-[180px]">{m.motivo}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold ${m.tipo === "ingreso" ? "text-[#065F46]" : m.tipo === "salida" ? "text-[#991B1B]" : "text-[#1D4ED8]"}`}>
                            {tipoLabel(m)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-[#065F46] font-medium">{m.entrada ? fmtNumber(m.entrada) : "—"}</td>
                        <td className="px-4 py-3 text-right text-[#991B1B] font-medium">{m.salida ? fmtNumber(m.salida) : "—"}</td>
                        <td className="px-4 py-3 text-right font-bold text-[#0F172A]">{fmtNumber(m.saldo_cantidad)}</td>
                        <td className="px-4 py-3 text-right text-[#475569]">{fmtMoney(m.costo_unitario)}</td>
                        <td className="px-4 py-3 text-right text-[#475569]">{fmtMoney(m.valor_movimiento)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-[#0F172A]">{fmtMoney(m.valor_acumulado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-md p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">{label}</p>
      <p className="text-2xl font-black tracking-tight text-[#0F172A] font-heading mt-1">{value}</p>
    </div>
  );
}
